import { lazy, Suspense, useEffect } from 'react'
import { Route, Routes, useLocation, type Location } from 'react-router'
import { AppShell } from '@/components/AppShell'
import { Home } from '@/routes/Home'
import { Suche } from '@/routes/Suche'
import { CatalogView } from '@/routes/CatalogView'
import { Settings } from '@/routes/Settings'
import { applyTheme, useApp } from '@/store/app'
import { Button, Empty, Spinner } from '@/components/primitives'
import { Hinweise } from '@/components/Hinweise'
import { sucheNeueFassung, useRueckkehr } from '@/features/updates/rueckkehr'

/**
 * Die Kartenansicht bringt pdf.js mit — gut 125 kB, die beim Start niemand braucht.
 * Erst beim Öffnen einer Karte nachgeladen; der Service Worker hat den Baustein
 * vorgeladen, sodass das auch ohne Netzverbindung funktioniert.
 */
const Card = lazy(() => import('@/routes/Card').then((m) => ({ default: m.Card })))

export function App() {
  const location = useLocation()
  /**
   * Die Suche hat eine eigene Adresse, aber keinen eigenen Bildschirm: Sie legt sich als
   * Blatt über die Ansicht, aus der sie gerufen wurde. Dazu merkt sich der Suchknopf in
   * `AppShell` die damalige Adresse im Zustand der neuen — das übliche Vorgehen bei
   * react-router — und die Routen werden für diese alte Adresse ausgewertet.
   *
   * Fehlt der Zustand, wurde `/suche` unmittelbar aufgerufen: über den Einsprungpunkt am
   * App-Symbol, einen geteilten Link oder ein Neuladen. Dann liegt die Startseite
   * darunter. So oder so gibt es nur einen Weg hierher und keine zweite Fassung der
   * Suche als bildschirmfüllende Ansicht, die auseinanderlaufen könnte.
   */
  const sucheOffen = location.pathname === '/suche'
  const hintergrund = (location.state as { hintergrund?: Location } | null)?.hintergrund
  const darunter = sucheOffen
    ? (hintergrund ?? { ...location, pathname: '/', search: '' })
    : location

  const status = useApp((s) => s.status)
  const error = useApp((s) => s.error)
  const init = useApp((s) => s.init)
  const refreshCatalog = useApp((s) => s.refreshCatalog)
  const theme = useApp((s) => s.theme)

  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  useEffect(() => {
    void init()
  }, [init])

  /**
   * Meldet der Service Worker neue Karten, während die App offen ist, wird der Katalog
   * sofort nachgezogen — sonst stünde der Hinweis erst beim nächsten Start da,
   * obwohl die Meldung gerade eben auf dem Bildschirm war.
   */
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    const beiNachricht = (event: MessageEvent) => {
      if ((event.data as { type?: string } | null)?.type === 'INHALT_GEAENDERT') {
        void refreshCatalog()
      }
    }

    navigator.serviceWorker.addEventListener('message', beiNachricht)
    return () => navigator.serviceWorker.removeEventListener('message', beiNachricht)
  }, [refreshCatalog])

  /**
   * Eine installierte App wird weggelegt und wieder hervorgeholt, nicht neu gestartet.
   * Was `init()` beim Start geprüft hat, wäre sonst für Tage der letzte Stand — und
   * zwar unauffällig, denn die App sähe dabei völlig in Ordnung aus.
   */
  useRueckkehr(() => {
    void refreshCatalog()
    void sucheNeueFassung()
  })

  if (status === 'lädt') {
    return (
      <div className="flex min-h-dvh items-center justify-center p-8">
        <Spinner label="Katalog wird geladen …" />
      </div>
    )
  }

  if (status === 'fehler') {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <Empty
          title="Start nicht möglich"
          hint={error ?? 'Unbekannter Fehler.'}
          action={<Button variant="primary" onClick={() => window.location.reload()}>Erneut versuchen</Button>}
        />
      </div>
    )
  }

  return (
    <>
      <Routes location={darunter}>
        <Route element={<AppShell />}>
          <Route index element={<Home />} />
          <Route path="katalog" element={<CatalogView />} />
          <Route
            path="karte/:id"
            element={
              <Suspense
                fallback={
                  <div className="flex min-h-[60dvh] items-center justify-center p-8">
                    <Spinner label="Karte wird geöffnet …" />
                  </div>
                }
              >
                <Card />
              </Suspense>
            }
          />
          <Route path="einstellungen" element={<Settings />} />
          <Route
            path="*"
            element={
              <div className="p-6 pt-10">
                <Empty title="Seite nicht gefunden" hint="Diese Adresse gibt es in der App nicht." />
              </div>
            }
          />
        </Route>
      </Routes>
      {sucheOffen && <Suche />}
      <Hinweise />
    </>
  )
}
